const OFM = "https://tiles.openfreemap.org";
const GLYPHS = `${OFM}/fonts/{fontstack}/{range}.pbf`;
const OMT = { type: "vector", url: `${OFM}/planet` };

export const STREETS_STYLE = `${OFM}/styles/positron`;

const WHITE_LABEL = { "text-color": "#ffffff", "text-halo-color": "rgba(10,10,10,0.8)", "text-halo-width": 1.4 };
const DARK_LABEL = { "text-color": "#3f3f46", "text-halo-color": "rgba(255,255,255,0.95)", "text-halo-width": 1.6 };

const placeLabels = (paint) => ({
  id: "place-labels", type: "symbol", source: "openmaptiles", "source-layer": "place",
  filter: ["in", ["get", "class"], ["literal", ["city", "town", "village", "suburb", "neighbourhood"]]],
  layout: {
    "text-field": ["coalesce", ["get", "name:en"], ["get", "name"]],
    "text-font": ["Noto Sans Regular"],
    "text-size": ["interpolate", ["linear"], ["zoom"], 8, 11, 14, 13],
    "text-transform": "uppercase",
    "text-letter-spacing": 0.14,
  },
  paint,
});

const roadLabels = (paint) => ({
  id: "road-labels", type: "symbol", source: "openmaptiles", "source-layer": "transportation_name", minzoom: 14,
  layout: { "symbol-placement": "line", "text-field": ["get", "name"], "text-font": ["Noto Sans Regular"], "text-size": 11 },
  paint,
});

/** Soft architectural massing used over both the satellite and the model styles. */
const buildings3d = (paint) => ({
  id: "buildings-3d", type: "fill-extrusion", source: "openmaptiles", "source-layer": "building", minzoom: 13,
  paint: {
    "fill-extrusion-height": ["interpolate", ["linear"], ["zoom"], 13, 0, 14.5, ["max", ["coalesce", ["get", "render_height"], 6], 4]],
    "fill-extrusion-base": ["coalesce", ["get", "render_min_height"], 0],
    "fill-extrusion-vertical-gradient": true,
    ...paint,
  },
});

/** High-resolution satellite imagery, raised buildings, atmospheric sky + fog for a cinematic 3D camera. */
export const satelliteStyle = {
  version: 8,
  glyphs: GLYPHS,
  sky: {
    "sky-color": "#9fc1e3",
    "sky-horizon-blend": 0.55,
    "horizon-color": "#eef2f6",
    "horizon-fog-blend": 0.75,
    "fog-color": "#e9eef3",
    "fog-ground-blend": 0.8,
  },
  sources: {
    satellite: {
      type: "raster",
      tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
      maxzoom: 19,
      attribution: "Imagery &copy; Esri, Maxar, Earthstar Geographics",
    },
    openmaptiles: OMT,
  },
  layers: [
    { id: "satellite", type: "raster", source: "satellite", paint: { "raster-saturation": -0.12, "raster-contrast": 0.08, "raster-brightness-max": 0.96, "raster-fade-duration": 250 } },
    {
      id: "roads-major", type: "line", source: "openmaptiles", "source-layer": "transportation", minzoom: 9, maxzoom: 14,
      filter: ["in", ["get", "class"], ["literal", ["motorway", "trunk"]]],
      paint: { "line-color": "rgba(255,255,255,0.35)", "line-width": 1.2 },
    },
    buildings3d({
      "fill-extrusion-color": ["interpolate", ["linear"], ["coalesce", ["get", "render_height"], 6], 0, "#e9e4da", 30, "#f6f3ee", 80, "#ffffff"],
      "fill-extrusion-opacity": 0.82,
    }),
    roadLabels(WHITE_LABEL),
    placeLabels(WHITE_LABEL),
  ],
};

/** Architectural white "scale model" — soft-lit massing with paper ground and pale water. */
export const modelStyle = {
  version: 8,
  glyphs: GLYPHS,
  light: { anchor: "viewport", color: "#ffffff", intensity: 0.32, position: [1.35, 210, 38] },
  sky: {
    "sky-color": "#dde6ee",
    "sky-horizon-blend": 0.6,
    "horizon-color": "#f5f4f0",
    "horizon-fog-blend": 0.8,
    "fog-color": "#f5f4f0",
    "fog-ground-blend": 0.85,
  },
  sources: { openmaptiles: OMT },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#ecebe6" } },
    {
      id: "landcover", type: "fill", source: "openmaptiles", "source-layer": "landcover",
      filter: ["in", ["get", "class"], ["literal", ["grass", "wood", "farmland", "wetland"]]],
      paint: { "fill-color": "#e1e5d8", "fill-opacity": 0.8 },
    },
    { id: "park", type: "fill", source: "openmaptiles", "source-layer": "park", paint: { "fill-color": "#dde4d3" } },
    { id: "water", type: "fill", source: "openmaptiles", "source-layer": "water", paint: { "fill-color": "#c3d0d8" } },
    { id: "waterway", type: "line", source: "openmaptiles", "source-layer": "waterway", paint: { "line-color": "#c3d0d8", "line-width": 1.2 } },
    {
      id: "aeroway", type: "fill", source: "openmaptiles", "source-layer": "aeroway",
      filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": "#d9d6cf" },
    },
    {
      id: "runway", type: "line", source: "openmaptiles", "source-layer": "aeroway",
      filter: ["in", ["get", "class"], ["literal", ["runway", "taxiway"]]],
      paint: { "line-color": "#cfccc4", "line-width": ["interpolate", ["exponential", 1.6], ["zoom"], 11, 2, 17, 40] },
    },
    {
      id: "roads", type: "line", source: "openmaptiles", "source-layer": "transportation",
      filter: ["in", ["get", "class"], ["literal", ["motorway", "trunk", "primary", "secondary", "tertiary", "minor", "service"]]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#ffffff",
        "line-width": ["interpolate", ["exponential", 1.5], ["zoom"], 9, ["match", ["get", "class"], ["motorway", "trunk"], 1.4, 0.3], 16,
          ["match", ["get", "class"], ["motorway", "trunk"], 14, ["primary", "secondary"], 9, 5]],
      },
    },
    buildings3d({
      "fill-extrusion-color": ["interpolate", ["linear"], ["coalesce", ["get", "render_height"], 6], 0, "#f4f2ee", 25, "#fbfaf8", 80, "#ffffff"],
      "fill-extrusion-opacity": 0.96,
    }),
    roadLabels(DARK_LABEL),
    placeLabels(DARK_LABEL),
  ],
};

export const STYLES = { satellite: satelliteStyle, model: modelStyle, streets: STREETS_STYLE };

const EMPTY = { type: "FeatureCollection", features: [] };
const BRASS = "#D4A72C";

/** Military land outlines, search radius ring and home-to-base link line. Re-added after every style load. */
export function addOverlays(map) {
  if (map.getSource("openmaptiles") && !map.getLayer("military-fill")) {
    const before = map.getLayer("buildings-3d") ? "buildings-3d" : undefined;
    map.addLayer({
      id: "military-fill", type: "fill", source: "openmaptiles", "source-layer": "landuse",
      filter: ["==", ["get", "class"], "military"], paint: { "fill-color": BRASS, "fill-opacity": 0.14 },
    }, before);
    map.addLayer({
      id: "military-line", type: "line", source: "openmaptiles", "source-layer": "landuse",
      filter: ["==", ["get", "class"], "military"],
      paint: { "line-color": BRASS, "line-width": ["interpolate", ["linear"], ["zoom"], 9, 1, 15, 2.4], "line-dasharray": [3, 2] },
    }, before);
  }
  if (!map.getSource("radius")) {
    const kind = (k) => ["==", ["get", "kind"], k];
    map.addSource("radius", { type: "geojson", data: EMPTY });
    map.addLayer({ id: "radius-fill", type: "fill", source: "radius", filter: kind("area"), paint: { "fill-color": "#0A0A0A", "fill-opacity": 0.08 } });
    map.addLayer({
      id: "radius-rings", type: "line", source: "radius", filter: ["all", kind("ring"), ["!", ["get", "active"]]],
      paint: { "line-color": "rgba(255,255,255,0.85)", "line-width": 1.2, "line-dasharray": [1, 2.5] },
    });
    map.addLayer({
      id: "radius-line", type: "line", source: "radius", filter: ["all", kind("ring"), ["get", "active"]],
      paint: { "line-color": BRASS, "line-width": 2.6, "line-dasharray": [2, 1.5] },
    });
    map.addLayer({
      id: "radius-labels", type: "symbol", source: "radius", filter: kind("label"),
      layout: { "text-field": ["get", "label"], "text-font": ["Noto Sans Bold"], "text-size": 12, "text-allow-overlap": true, "text-letter-spacing": 0.08 },
      paint: {
        "text-color": ["case", ["get", "active"], "#0A0A0A", "#ffffff"],
        "text-halo-color": ["case", ["get", "active"], BRASS, "rgba(10,10,10,0.85)"],
        "text-halo-width": 3,
      },
    });
  }
  if (!map.getSource("link")) {
    map.addSource("link", { type: "geojson", data: EMPTY });
    map.addLayer({
      id: "link-casing", type: "line", source: "link", layout: { "line-cap": "round" },
      paint: { "line-color": "#0A0A0A", "line-width": 7, "line-opacity": 0.55 },
    });
    map.addLayer({
      id: "link-line", type: "line", source: "link", layout: { "line-cap": "round" },
      paint: { "line-color": BRASS, "line-width": 3.5, "line-dasharray": [1.5, 1.5] },
    });
    map.addLayer({
      id: "link-label", type: "symbol", source: "link",
      layout: {
        "symbol-placement": "line-center", "text-field": ["get", "label"], "text-font": ["Noto Sans Bold"],
        "text-size": 13, "text-offset": [0, -0.9], "text-allow-overlap": true,
      },
      paint: { "text-color": "#0A0A0A", "text-halo-color": "#FFFFFF", "text-halo-width": 3 },
    });
  }
}

export function setOverlayData(map, { radius, link }) {
  map.getSource("radius")?.setData(radius || EMPTY);
  map.getSource("link")?.setData(link || EMPTY);
}
